import BusyType from 'owa-service/lib/contract/BusyType';
import CalendarItem from 'owa-service/lib/contract/CalendarItem';
import CalendarItemExperienceType from 'owa-service/lib/contract/CalendarItemExperienceType';
import ResponseTypeType from 'owa-service/lib/contract/ResponseTypeType';
import { parse, getEwsRequestString, getDisplayDateFromEwsDate } from 'owa-datetime';
import CalendarEvent, {
    createEmptyCalendarEvent,
} from 'owa-calendar-types/lib/types/CalendarEvent';
import { getUserMailboxInfo, MailboxInfo } from 'owa-client-ids';
import getTxpObjectFromTxpEntities from 'owa-txp/lib/utils/getTxpObjectFromTxpEntities';
import { getConfig } from 'owa-service/lib/config';
import { isFeatureEnabled } from 'owa-feature-flags';

/**
 * Converts a CalendarItem instance to CalendarEvent. This function will be used mostly from service
 * functions to convert the returned CalendarItem service objects to CalendarEvent instances for
 * consumption by the application.
 * @param calendarItem The calendar item (service object) to convert
 * @param mailboxInfo The mailbox info for the calendar event
 * @returns The converted CalendarEvent object
 */
export function calendarEvent(
    calendarItem: CalendarItem,
    mailboxInfo?: MailboxInfo
): CalendarEvent {
    // If no mailboxInfo is provided then get the user mailbox info
    mailboxInfo = mailboxInfo || getUserMailboxInfo();

    // Create empty instance of CalendarEvent so that mobX doesn't freak out
    let emptyCalendarEvent = createEmptyCalendarEvent();

    // Copy then overwrite with proper conversions
    let calendarEvent = {
        ...emptyCalendarEvent,
        ...calendarItem,
        ...{
            ItemId: calendarItem.ItemId
                ? { ...calendarItem.ItemId, mailboxInfo: mailboxInfo }
                : calendarItem.ItemId,
            ParentFolderId: calendarItem.ParentFolderId
                ? { ...calendarItem.ParentFolderId, mailboxInfo: mailboxInfo }
                : calendarItem.ParentFolderId,
            Start: parseCalendarItemDate(calendarItem.Start),
            End: parseCalendarItemDate(calendarItem.End),
            LastModifiedTime: parseCalendarItemDate(calendarItem.LastModifiedTime),
            FreeBusyType: calendarItem.FreeBusyType as BusyType,
            ResponseType: calendarItem.ResponseType as ResponseTypeType,
            AppointmentReplyTime: parseCalendarItemDate(calendarItem.AppointmentReplyTime),
            ExperienceType: calendarItem.ExperienceType
                ? calendarItem.ExperienceType
                : ('Unspecified' as CalendarItemExperienceType),
            TxpObject: calendarItem.TailoredXpEntities
                ? getTxpObjectFromTxpEntities(
                      calendarItem.TailoredXpEntities,
                      calendarItem.EntityNamesMap
                  )
                : undefined,
        },
    } as CalendarEvent;

    return calendarEvent ? calendarEvent : null;
}

function parseCalendarItemDate(date?: string) {
    // All production calls to calendarEvent() are done in response to
    // an EWS call or notification, which returns data in the user's time zone.
    // We can parse making this assumption, to handle dates outside the known DST
    // ranges without losing the offset returned by the server.
    if (date) {
        if (isFeatureEnabled('cal-assumeTzOffsetFromCalendarItem')) {
            const assumeDatesAreInThisTz = getConfig().timezone;
            const assumeOffsetFromStringIsCorrect = !!assumeDatesAreInThisTz;
            return parse(assumeDatesAreInThisTz, date, assumeOffsetFromStringIsCorrect);
        }
        return getDisplayDateFromEwsDate(date);
    }
    return undefined;
}

/**
 * Converts a CalendarEvent instance to CalendarItem. This function for the most part will be called
 * only within functions that deal directly with making service requests. Other code should only use
 * CalendarEvent instances.
 * @param calendarEvent The CalendarEvent object
 * @returns The converted CalendarItem instance
 */
export function toCalendarItem(calendarEvent: CalendarEvent): CalendarItem {
    return {
        __type: 'CalendarItem:#Exchange',
        ...calendarEvent,
        Start: calendarEvent.Start && getEwsRequestString(calendarEvent.Start),
        End: calendarEvent.End && getEwsRequestString(calendarEvent.End),
        LastModifiedTime:
            calendarEvent.LastModifiedTime && getEwsRequestString(calendarEvent.LastModifiedTime),
        AppointmentReplyTime:
            calendarEvent.AppointmentReplyTime &&
            getEwsRequestString(calendarEvent.AppointmentReplyTime),
        FreeBusyType: calendarEvent.FreeBusyType as string,
        CharmId: calendarEvent.CharmId,
        IsDraft: calendarEvent.IsDraft,
    } as CalendarItem;
}
